import { cn } from '@/lib/utils';
import dayjs from 'dayjs';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon, XIcon } from 'lucide-react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type DateRange } from 'react-day-picker';
import { PatternFormat } from 'react-number-format';
import { useMediaQuery } from '../hooks/useMediaQuery';
import {
  type DateRangeOutput,
  type DateRangePickerProps,
  formatDateForDisplay,
  getShortcutRange,
  parseDateInput,
  type ShortcutOption,
  toDayjs,
  toOutputType,
  validateDateRange,
} from './date-picker-types';
import { FieldErrors } from './form/FieldErrors';
import { Icon } from './Icon';
import { Icons } from './icons';
import { Button } from './ui/button';
import { Calendar } from './ui/calendar';
import { Label } from './ui/label';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

// Move expensive function outside to prevent recreation
const getShortcutLabel = (shortcut: ShortcutOption): string => {
  if (typeof shortcut === 'string') {
    return shortcut.charAt(0).toUpperCase() + shortcut.slice(1).replace(/([A-Z])/g, ' $1');
  }

  return shortcut.label;
};

const ShortcutsSection = memo(
  ({
    shortcuts,
    onShortcut,
  }: {
    shortcuts: ShortcutOption[];
    onShortcut: (shortcut: ShortcutOption) => void;
  }) => {
    if (!shortcuts?.length) return null;

    return (
      <div className="border-t border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-dark-gray-600 p-3 w-0 min-w-full">
        <div className="flex flex-wrap gap-2">
          {shortcuts.map((shortcut, index) => (
            <Button
              key={index}
              type="button"
              variant="outline"
              size="sm"
              className="h-8 text-xs font-normal"
              onClick={() => onShortcut(shortcut)}
            >
              {getShortcutLabel(shortcut)}
            </Button>
          ))}
        </div>
      </div>
    );
  }
);

const DateRangeWarning = memo(
  ({
    validation,
    onDismiss,
  }: {
    validation: { isValid: boolean; actualDays: number; maxDays: number };
    onDismiss: () => void;
  }) => {
    if (validation.isValid) return null;

    return (
      <div className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border-t border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200">
        <Icon icon={Icons.trash} className="h-4 w-4 shrink-0" />
        <div className="flex-1 text-sm">
          Date range too large: {validation.actualDays} days selected, maximum {validation.maxDays}{' '}
          days allowed.
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-auto p-1 text-amber-600 hover:text-amber-800 dark:text-amber-300 dark:hover:text-amber-100"
          onClick={onDismiss}
        >
          <XIcon className="h-3 w-3" />
        </Button>
      </div>
    );
  }
);

const CalendarContent = memo(
  ({
    from,
    tempRange,
    onSelect,
    disabled,
    onCancel,
    onOk,
    shortcuts,
    onShortcut,
    requireConfirm = false,
    maxDateRange,
    rangeValidation,
    onDismissWarning,
    clearable,
    hasValue,
    onClear,
  }: {
    from?: dayjs.Dayjs;
    tempRange: DateRange | undefined;
    onSelect: (range: DateRange | undefined) => void;
    disabled: boolean;
    onCancel: () => void;
    onOk: () => void;
    shortcuts: ShortcutOption[];
    onShortcut: (shortcut: ShortcutOption) => void;
    requireConfirm?: boolean;
    maxDateRange?: number;
    rangeValidation?: { isValid: boolean; actualDays: number; maxDays: number };
    onDismissWarning: () => void;
    clearable?: boolean;
    hasValue?: boolean;
    onClear?: (e: React.MouseEvent) => void;
  }) => {
    const defaultMonth = useMemo(() => from?.toDate(), [from]);

    return (
      <PopoverContent
        className="w-auto p-0 bg-white dark:bg-dark-gray-700 border-gray-300 dark:border-gray-600"
        align="start"
        sideOffset={4}
        data-component="date-range-picker-popover"
      >
        <div className="rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm bg-white dark:bg-dark-gray-700">
          <div className="w-fit">
            <Calendar
              mode="range"
              defaultMonth={defaultMonth}
              selected={tempRange}
              onSelect={onSelect}
              numberOfMonths={useMediaQuery('(max-width: 768px)') ? 1 : 2}
              className="rounded-t-lg"
              disabled={disabled}
              fixedWeeks
            />
            {rangeValidation && !rangeValidation.isValid && (
              <DateRangeWarning validation={rangeValidation} onDismiss={onDismissWarning} />
            )}
            {requireConfirm && (
              <div className="flex items-center justify-end gap-2 p-3 border-t border-gray-300 dark:border-gray-600">
                <Button type="button" variant="outline" size="sm" onClick={onCancel}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={onOk}
                  disabled={
                    !tempRange?.from || disabled || (rangeValidation && !rangeValidation.isValid)
                  }
                >
                  OK
                </Button>
              </div>
            )}
          </div>
          <ShortcutsSection shortcuts={shortcuts} onShortcut={onShortcut} />
          {clearable && hasValue && onClear && (
            <div className="border-t border-gray-300 dark:border-gray-600 p-3 w-0 min-w-full">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 text-xs font-normal"
                onClick={onClear}
              >
                Clear
              </Button>
            </div>
          )}
        </div>
      </PopoverContent>
    );
  }
);

const TodayButton = memo(
  ({
    showNavigation,
    disabled,
    onToday,
  }: {
    showNavigation: boolean;
    disabled: boolean;
    onToday: () => void;
  }) =>
    showNavigation ? (
      <Button
        type="button"
        variant="link"
        size="sm"
        className="h-auto p-0 text-xs text-primary hover:text-primary/80"
        onClick={onToday}
        disabled={disabled}
        data-component="date-range-picker-today-btn"
      >
        Today
      </Button>
    ) : null
);

// Shared input class for the PatternFormat fields
const dateInputClass =
  'bg-gray-50 dark:bg-dark-gray-600 rounded-md px-1.5 py-0.5 text-sm w-24 outline-none border border-transparent placeholder:text-xs placeholder:text-muted-foreground dark:placeholder:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-gray-500 focus:bg-white dark:focus:bg-dark-gray-700 focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400 cursor-text transition-all';

export function DateRangePicker<T extends 'number' | 'string' | 'dayjs' | 'date' = 'dayjs'>({
  label,
  className,
  value,
  onChange,
  placeholder = 'Pick a date range',
  displayFormat = 'MM/DD/YYYY',
  outputFormat = 'YYYY-MM-DD',
  outputType = 'dayjs' as T,
  showNavigation = false,
  showIcon = true,
  shortcuts = [],
  disabled = false,
  errors,
  clearable = false,
  onClear,
  requireConfirm = false,
  maxDateRange,
  allowSameDay = false,
}: DateRangePickerProps<T>) {
  const [open, setOpen] = useState(false);
  const [tempRange, setTempRange] = useState<DateRange | undefined>({
    from: value?.from ? new Date(value.from.toString()) : undefined,
    to: value?.to ? new Date(value.to.toString()) : undefined,
  });
  const [showWarning, setShowWarning] = useState(false);

  // Typed input state
  const [fromInputValue, setFromInputValue] = useState('');
  const [toInputValue, setToInputValue] = useState('');
  const [isFromTyping, setIsFromTyping] = useState(false);
  const [isToTyping, setIsToTyping] = useState(false);
  const toInputRef = useRef<HTMLInputElement>(null);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const commitTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Cleanup timer on unmount
  useEffect(() => () => clearTimeout(commitTimer.current), []);

  const from = useMemo(() => (value?.from ? toDayjs(value.from) : undefined), [value?.from]);
  const to = useMemo(() => (value?.to ? toDayjs(value.to) : undefined), [value?.to]);

  // Sync input values from value prop when user is not typing
  useEffect(() => {
    if (!isFromTyping) {
      setFromInputValue(formatDateForDisplay(from, displayFormat));
    }
  }, [from, displayFormat, isFromTyping]);

  useEffect(() => {
    if (!isToTyping) {
      setToInputValue(formatDateForDisplay(to, displayFormat));
    }
  }, [to, displayFormat, isToTyping]);

  // Update temp range when value prop changes
  useEffect(() => {
    setTempRange({
      from: value?.from ? toDayjs(value.from)?.toDate() : undefined,
      to: value?.to ? toDayjs(value.to)?.toDate() : undefined,
    });
  }, [value?.from, value?.to]);

  // Validate the current temp range
  const rangeValidation = useMemo(() => {
    if (!maxDateRange || !tempRange?.from || !tempRange?.to) {
      return { isValid: true, actualDays: 0, maxDays: maxDateRange || 0 };
    }

    return validateDateRange(tempRange.from, tempRange.to, maxDateRange);
  }, [tempRange?.from, tempRange?.to, maxDateRange]);

  // Emit an onChange with auto-swap
  const emitRange = useCallback(
    (newFrom: dayjs.Dayjs, newTo: dayjs.Dayjs) => {
      // Auto-swap if from > to
      let finalFrom = newFrom;
      let finalTo = newTo;

      if (finalFrom.isAfter(finalTo)) {
        [finalFrom, finalTo] = [finalTo, finalFrom];
      }

      // Validate against maxDateRange
      if (maxDateRange) {
        const validation = validateDateRange(finalFrom.toDate(), finalTo.toDate(), maxDateRange);

        if (!validation.isValid) {
          setTempRange({ from: finalFrom.toDate(), to: finalTo.toDate() });
          setShowWarning(true);

          return;
        }
      }

      setShowWarning(false);
      onChange?.({
        from: toOutputType(finalFrom, outputType, outputFormat),
        to: toOutputType(finalTo, outputType, outputFormat),
      } as DateRangeOutput<T>);
    },
    [maxDateRange, onChange, outputType, outputFormat]
  );

  // Commit both inputs when focus leaves the container
  const commitInputs = useCallback(() => {
    const parsedFrom = parseDateInput(fromInputValue, displayFormat);
    const parsedTo = parseDateInput(toInputValue, displayFormat);

    const newFrom = parsedFrom || from;
    const newTo = parsedTo || to;

    if (newFrom && newTo) {
      const fromChanged = !from || !newFrom.isSame(from, 'day');
      const toChanged = !to || !newTo.isSame(to, 'day');

      if (fromChanged || toChanged) {
        emitRange(newFrom, newTo);
      }
    }

    // Revert invalid inputs
    if (!parsedFrom) {
      setFromInputValue(formatDateForDisplay(from, displayFormat));
    }

    if (!parsedTo) {
      setToInputValue(formatDateForDisplay(to, displayFormat));
    }

    setIsFromTyping(false);
    setIsToTyping(false);
  }, [fromInputValue, toInputValue, displayFormat, from, to, emitRange]);

  // Container-level blur: only commit when focus leaves the entire input area
  const handleContainerBlur = useCallback(
    (e: React.FocusEvent) => {
      clearTimeout(commitTimer.current);
      commitTimer.current = setTimeout(() => {
        const active = document.activeElement;
        const inContainer = inputContainerRef.current?.contains(active);
        const inPopover = active?.closest('[data-component="date-range-picker-popover"]');

        if (!inContainer && !inPopover) {
          commitInputs();
        }
      }, 0);
    },
    [commitInputs]
  );

  // "From" input handlers
  const handleFromInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFromInputValue(e.target.value);
    setIsFromTyping(true);
  }, []);

  // "To" input handlers
  const handleToInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setToInputValue(e.target.value);
    setIsToTyping(true);
  }, []);

  // Enter on "from" advances to "to"; Enter on "to" commits both
  const handleFromKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        toInputRef.current?.focus();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setIsFromTyping(false);
        setFromInputValue(formatDateForDisplay(from, displayFormat));
        (e.target as HTMLInputElement).blur();
      }
    },
    [from, displayFormat]
  );

  const handleToKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commitInputs();
        (e.target as HTMLInputElement).blur();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setIsToTyping(false);
        setToInputValue(formatDateForDisplay(to, displayFormat));
        (e.target as HTMLInputElement).blur();
      }
    },
    [to, displayFormat, commitInputs]
  );

  // Auto-advance: when "from" is fully typed, focus "to"
  useEffect(() => {
    if (isFromTyping && fromInputValue.length === 10 && !fromInputValue.includes('_')) {
      toInputRef.current?.focus();
    }
  }, [fromInputValue, isFromTyping]);

  const handleInputFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  }, []);

  const handleSelect = useCallback(
    (range: DateRange | undefined) => {
      const alreadyHasRange = tempRange?.from && tempRange?.to;

      if (alreadyHasRange) {
        const justToday = range?.from?.getTime() === range?.to?.getTime();

        if (justToday) {
          if (allowSameDay) {
            const calFrom = range?.from;
            const calTo = range?.to;

            const validation = maxDateRange
              ? validateDateRange(calFrom, calTo, maxDateRange)
              : { isValid: true, actualDays: 0, maxDays: 0 };

            if (!validation.isValid) {
              setTempRange({ from: calFrom, to: calTo });
              setShowWarning(true);

              return;
            }

            setShowWarning(false);

            if (requireConfirm) {
              setTempRange({ from: calFrom, to: calTo });
            } else {
              onChange?.({
                from: toOutputType(toDayjs(calFrom), outputType, outputFormat),
                to: toOutputType(toDayjs(calTo), outputType, outputFormat),
              } as DateRangeOutput<T>);
              setOpen(false);
            }

            return;
          } else {
            setTempRange({ from: tempRange?.from, to: tempRange?.to });

            return;
          }
        }

        const fromIsDifferent = range?.from?.getTime() !== tempRange?.from?.getTime();
        setTempRange({ from: fromIsDifferent ? range?.from : range?.to, to: undefined });
        setShowWarning(false);

        return;
      } else if (!tempRange?.from && !tempRange?.to) {
        setTempRange({ from: range?.from, to: undefined });
        setShowWarning(false);

        return;
      }

      const calFrom = range?.from ?? tempRange?.from ?? tempRange?.to;
      const calTo = range?.to ?? tempRange?.to ?? tempRange?.from;

      const validation = maxDateRange
        ? validateDateRange(calFrom, calTo, maxDateRange)
        : { isValid: true, actualDays: 0, maxDays: 0 };

      if (!validation.isValid) {
        setTempRange({ from: calFrom, to: calTo });
        setShowWarning(true);

        return;
      }

      setShowWarning(false);

      if (requireConfirm) {
        setTempRange({ from: calFrom, to: calTo });
      } else {
        onChange?.({
          from: toOutputType(toDayjs(calFrom), outputType, outputFormat),
          to: toOutputType(toDayjs(calTo), outputType, outputFormat),
        } as DateRangeOutput<T>);
        setOpen(false);
      }
    },
    [tempRange, maxDateRange, requireConfirm, onChange, outputType, outputFormat, allowSameDay]
  );

  const handleOk = useCallback(() => {
    if (tempRange?.from) {
      const okFrom = dayjs(tempRange.from);
      const okTo = tempRange.to ? dayjs(tempRange.to) : okFrom;

      const validation = maxDateRange
        ? validateDateRange(tempRange.from, tempRange.to, maxDateRange)
        : { isValid: true, actualDays: 0, maxDays: 0 };

      if (!validation.isValid) {
        return;
      }

      onChange?.({
        from: toOutputType(okFrom, outputType, outputFormat),
        to: toOutputType(okTo, outputType, outputFormat),
      } as DateRangeOutput<T>);
    } else {
      onChange?.(undefined);
    }

    setOpen(false);
    setShowWarning(false);
  }, [tempRange, onChange, outputType, outputFormat, maxDateRange]);

  const handleDismissWarning = useCallback(() => {
    setShowWarning(false);
    setTempRange({
      from: value?.from ? new Date(value.from.toString()) : undefined,
      to: value?.to ? new Date(value.to.toString()) : undefined,
    });
  }, [value?.from, value?.to]);

  const handleCancel = useCallback(() => {
    setTempRange({
      from: value?.from ? new Date(value.from.toString()) : undefined,
      to: value?.to ? new Date(value.to.toString()) : undefined,
    });
    setOpen(false);
    setShowWarning(false);
  }, [value]);

  const handleShortcut = useCallback(
    (shortcut: ShortcutOption) => {
      const range = getShortcutRange(shortcut);

      const validation = maxDateRange
        ? validateDateRange(range.from.toDate(), range.to.toDate(), maxDateRange)
        : { isValid: true, actualDays: 0, maxDays: 0 };

      if (!validation.isValid) {
        setTempRange({ from: range.from.toDate(), to: range.to.toDate() });
        setShowWarning(true);

        return;
      }

      onChange?.({
        from: toOutputType(range.from, outputType, outputFormat),
        to: toOutputType(range.to, outputType, outputFormat),
      } as DateRangeOutput<T>);
      setOpen(false);
      setShowWarning(false);
    },
    [onChange, outputType, outputFormat, maxDateRange]
  );

  const handlePrevious = useCallback(() => {
    if (!value?.from || !value?.to) return;

    const prevFrom = toDayjs(value.from);
    const prevTo = toDayjs(value.to);

    if (!prevFrom || !prevTo) return;

    const daysBetween = prevTo.diff(prevFrom, 'day');

    const newFrom = prevFrom.subtract(daysBetween + 1, 'day');
    const newTo = prevTo.subtract(daysBetween + 1, 'day');

    onChange?.({
      from: toOutputType(newFrom, outputType, outputFormat),
      to: toOutputType(newTo, outputType, outputFormat),
    } as DateRangeOutput<T>);
  }, [value, onChange, outputType, outputFormat]);

  const handleNext = useCallback(() => {
    if (!value?.from || !value?.to) return;

    const nextFrom = toDayjs(value.from);
    const nextTo = toDayjs(value.to);

    if (!nextFrom || !nextTo) return;

    const daysBetween = nextTo.diff(nextFrom, 'day');

    const newFrom = nextFrom.add(daysBetween + 1, 'day');
    const newTo = nextTo.add(daysBetween + 1, 'day');

    onChange?.({
      from: toOutputType(newFrom, outputType, outputFormat),
      to: toOutputType(newTo, outputType, outputFormat),
    } as DateRangeOutput<T>);
  }, [value, onChange, outputType, outputFormat]);

  const handleToday = useCallback(() => {
    const today = dayjs();
    onChange?.({
      from: toOutputType(today, outputType, outputFormat),
      to: toOutputType(today, outputType, outputFormat),
    } as DateRangeOutput<T>);
  }, [onChange, outputType, outputFormat]);

  const hasValue = value?.from && value?.to;

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onChange?.({
        from: undefined,
        to: undefined,
      });

      if (onClear) {
        onClear();
      }
    },
    [onChange, onClear]
  );

  // The two PatternFormat inputs + calendar icon + clear button
  const dateInputs = (
    <>
      <PatternFormat
        format="##/##/####"
        mask="_"
        value={fromInputValue}
        onChange={handleFromInputChange}
        onKeyDown={handleFromKeyDown}
        onFocus={handleInputFocus}
        placeholder="MM/DD/YYYY"
        disabled={disabled}
        className={dateInputClass}
        autoComplete="off"
        data-form-type="other"
        data-lpignore="true"
        data-1p-ignore="true"
      />
      <span className="text-muted-foreground dark:text-gray-400 mx-0.5 select-none shrink-0">
        –
      </span>
      <PatternFormat
        format="##/##/####"
        mask="_"
        value={toInputValue}
        onChange={handleToInputChange}
        onKeyDown={handleToKeyDown}
        onFocus={handleInputFocus}
        placeholder="MM/DD/YYYY"
        disabled={disabled}
        className={dateInputClass}
        getInputRef={toInputRef}
        autoComplete="off"
        data-form-type="other"
        data-lpignore="true"
        data-1p-ignore="true"
      />
    </>
  );

  return (
    <div className={cn('flex flex-col gap-1', className)} data-component="date-range-picker-root">
      {label && <Label className="text-sm font-medium">{label}</Label>}
      {showNavigation ? (
        <div className="flex flex-col gap-1">
          <div
            className="relative flex items-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm w-fit"
            data-component="date-range-picker-wrapper"
          >
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-sm rounded-r-none border border-gray-300 dark:border-gray-600 bg-white dark:bg-dark-gray-700 h-9 px-3"
              onClick={handlePrevious}
              disabled={disabled}
              data-component="date-range-picker-prev-btn"
            >
              <ChevronLeftIcon className="h-4 w-4" />
              <span className="sr-only">Previous</span>
            </Button>

            <div
              ref={inputContainerRef}
              onBlur={handleContainerBlur}
              className={cn(
                'flex items-center h-9 px-2 bg-white dark:bg-dark-gray-700 border-x-0 min-w-[200px]',
                disabled && 'pointer-events-none opacity-50'
              )}
            >
              {dateInputs}

              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <div
                    className={cn(
                      'shrink-0 ml-1 cursor-pointer text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors',
                      disabled && 'pointer-events-none'
                    )}
                  >
                    <CalendarIcon className="h-4 w-4" />
                  </div>
                </PopoverTrigger>
                <CalendarContent
                  from={from}
                  tempRange={tempRange}
                  onSelect={handleSelect}
                  disabled={disabled}
                  onCancel={handleCancel}
                  onOk={handleOk}
                  shortcuts={shortcuts}
                  onShortcut={handleShortcut}
                  requireConfirm={requireConfirm}
                  maxDateRange={maxDateRange}
                  rangeValidation={showWarning ? rangeValidation : undefined}
                  onDismissWarning={handleDismissWarning}
                  clearable={clearable}
                  hasValue={!!hasValue}
                  onClear={handleClear}
                />
              </Popover>
            </div>

            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-sm rounded-l-none border border-gray-300 dark:border-gray-600 bg-white dark:bg-dark-gray-700 h-9 px-3"
              onClick={handleNext}
              disabled={disabled}
              data-component="date-range-picker-next-btn"
            >
              <ChevronRightIcon className="h-4 w-4" />
              <span className="sr-only">Next</span>
            </Button>
          </div>
          <FieldErrors errors={errors} />
          <TodayButton showNavigation={showNavigation} disabled={disabled} onToday={handleToday} />
        </div>
      ) : (
        <div>
          <div
            ref={inputContainerRef}
            onBlur={handleContainerBlur}
            className={cn(
              'flex items-center h-9 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-dark-gray-700 px-1 shadow-xs',
              disabled && 'pointer-events-none opacity-50 cursor-not-allowed',
              errors ? 'border-destructive ring-destructive/20 dark:ring-destructive/40' : ''
            )}
            data-component="date-range-picker-trigger"
          >
            {dateInputs}

            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <div
                  className={cn(
                    'shrink-0 ml-auto cursor-pointer text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors',
                    disabled && 'pointer-events-none'
                  )}
                >
                  <CalendarIcon className="h-4 w-4" />
                </div>
              </PopoverTrigger>
              <CalendarContent
                from={from}
                tempRange={tempRange}
                onSelect={handleSelect}
                disabled={disabled}
                onCancel={handleCancel}
                onOk={handleOk}
                shortcuts={shortcuts}
                onShortcut={handleShortcut}
                requireConfirm={requireConfirm}
                maxDateRange={maxDateRange}
                rangeValidation={showWarning ? rangeValidation : undefined}
                onDismissWarning={handleDismissWarning}
                clearable={clearable}
                hasValue={!!hasValue}
                onClear={handleClear}
              />
            </Popover>
          </div>
          <FieldErrors errors={errors} />
        </div>
      )}
    </div>
  );
}
